import { clsx } from "clsx";

import styles from "./RVPanel.module.scss";

/**
 * Preset sizes for {@link RVPanel}. Controls the panel width via the matching
 * SCSS module class.
 */
// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
enum Size {
    default = "default",
    quarter = "quarter",
    half = "half",
    full = "full",
    px400 = "px-400",
}

/**
 * Props for {@link RVPanel}. Any additional props are spread onto the
 * underlying `div`.
 */
export type RVPanelProps = {
    /** Content rendered inside the panel */
    children?: any;
    /** Additional CSS class name applied to the panel container */
    className?: string;
    /** Preset width for the panel. Defaults to {@link Size.default}. */
    size?: Size;
    /** When true, applies a drop shadow to the panel */
    shaded?: boolean;
    /** When true, removes the left margin so the panel snaps to the left edge */
    snapLeft?: boolean;
    /** When true, removes the internal padding */
    noPadding?: boolean;
    /** When true, unsets `justify-content` on the panel container */
    justifyContentUnset?: boolean;
    /** Additional props spread onto the container div */
    [x: string]: any;
};

/**
 * A themed container `div` that keeps panels consistent in size and surface
 * styling (background color, border radius, padding, and optional shadow).
 *
 * @example
 * ```tsx
 * // Fixed 400px panel with a shadow
 * <RVPanel size={RVPanel.Size.px400} shaded>
 *   <ResetPasswordForm />
 * </RVPanel>
 *
 * // Full-width panel with no padding
 * <RVPanel size={RVPanel.Size.full} noPadding>
 *   <Content />
 * </RVPanel>
 * ```
 */
function RVPanel({
    children,
    className,
    size = Size.default,
    shaded = false,
    snapLeft = false,
    noPadding = false,
    justifyContentUnset = false,
    ...props
}: RVPanelProps): JSX.Element {
    return (
        <div
            className={clsx(styles[size], className, {
                [styles.shaded]: shaded,
                [styles.snapLeft]: snapLeft,
                [styles.noPadding]: noPadding,
                [styles.justifyContentUnset]: justifyContentUnset,
            })}
            {...props}
        >
            {children}
        </div>
    );
}

const RVPanelWithSize = Object.assign(RVPanel, { Size });

export { RVPanelWithSize as RVPanel };
